// src/utils/AuthHelpers.js
//
// Shared authentication/authorization helpers.
// Every route guard in the app should call into THIS file only —
// do not re-implement "am I logged in" or "am I an admin" checks
// anywhere else. If you need a new access rule, add a function here.

import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

/**
 * Firebase restores the logged-in user asynchronously on page load.
 * That means `auth.currentUser` can be `null` for a brief moment even
 * when the person IS actually logged in (their session just hasn't
 * finished loading yet). If we trust `auth.currentUser` immediately,
 * a logged-in user who refreshes an admin page gets kicked out.
 *
 * This function waits for Firebase to finish that initial check
 * before we make any access decision.
 */
const waitForAuthUser = () => {
    return new Promise((resolve) => {
        if (auth.currentUser !== null) {
            resolve(auth.currentUser);
            return;
        }

        const unsubscribe = auth.onAuthStateChanged((user) => {
            unsubscribe();
            resolve(user);
        });
    });
};

/**
 * Is the current visitor logged in at all?
 * Returns the Firebase user object, or null if nobody is signed in.
 */
export async function getCurrentUser() {
    return waitForAuthUser();
}

/**
 * Is the current visitor an admin?
 * Looks up their Firestore profile (users/{uid}) and checks role === "admin".
 * Returns false for logged-out visitors and for regular users.
 */
export async function checkAdmin() {
    const user = await waitForAuthUser();
    if (!user) return false;

    const snapshot = await getDoc(doc(db, "users", user.uid));
    return snapshot.exists() && snapshot.data().role === "admin";
}

/**
 * What is the current visitor allowed to see in the Course Hub?
 *
 * Reads the same users/{uid} document checkAdmin() reads, for three
 * fields that signup/payment should write when an account is created
 * or upgraded:
 *   - enrolledTracks: string[]   e.g. ["frontend"] — profession ids
 *     from AiCourseAssistancePage.jsx's PROFESSIONS list
 *   - supportLevel: string|null  "self-study" | "partially-guided" |
 *     "fully-guided" — which plan tier from PricingPage.jsx
 *   - allAccess: boolean         true if they bought the All-Access
 *     bundle rather than a single track
 *
 * Always returns a safe, fully-shaped object — logged-out visitors
 * and users with no Firestore doc yet get zero access rather than a
 * thrown error, so a caller never has to null-check this by hand.
 *
 * Admins (role === "admin", the same field checkAdmin() reads) get
 * allAccess: true automatically — an admin account has no reason to
 * also carry enrolledTracks, so without this check it was being read
 * as a zero-access student and shown only the shared/fail-open
 * categories, same as anyone else with no track set. FIXED.
 *
 * NOTE: nothing currently WRITES enrolledTracks/supportLevel/allAccess
 * for regular students. That has to happen in the signup/payment-
 * completion flow — see the comment at the top of PricingPage.jsx and
 * AiCourseAssistancePage.jsx for what each page passes forward today.
 * Until that's wired up, every non-admin student will see ONLY the
 * shared/fail-open categories (DevOps, QA, Core Practices, etc.),
 * regardless of which track they picked or paid for — this is the
 * same gap already flagged, now confirmed in testing.
 */
export async function getUserAccess() {
    const user = await waitForAuthUser();
    const empty = { enrolledTracks: [], supportLevel: null, allAccess: false };
    if (!user) return empty;

    const snapshot = await getDoc(doc(db, "users", user.uid));
    if (!snapshot.exists()) return empty;

    const data = snapshot.data();

    if (data.role === "admin") {
        return { enrolledTracks: [], supportLevel: null, allAccess: true };
    }

    return {
        enrolledTracks: Array.isArray(data.enrolledTracks) ? data.enrolledTracks : [],
        supportLevel: data.supportLevel || null,
        allAccess: data.allAccess === true,
    };
}
